import React from "react";
import { useStressEntryContext } from "./StressEntryContext";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { FormFocusError } from "../../../common/FormFocusError";
import { TextField as FormikTextField, TextField } from "formik-material-ui";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";

import {
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  useTheme,
  Typography,
  Backdrop,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import {
  postPauseReume,
  useValidateAssetSerial,
} from "../../../testCommunication/common/testMutations";
import { useUnitsBySerialNumber } from "../../../testCommunication/common/testQueries";

import { useAuthContext } from "../../../common/AuthContext";
import { useFetchContext } from "../../../common/FetchContext";
import { processErrorOnMutation } from "../../../../util/errorMessaging";
import { useHistory } from "react-router-dom";
import { filter, find } from "lodash";
import {
  STEP_RESULT_TEST_END_NAME,
  STEP_RESULT_TEST_PAUSE_NAME,
  STEP_RESULT_TEST_RESUME_NAME,
  STEP_RESULT_TEST_START_NAME,
  STRESSOR_CHECK_IN_MODE,
  STRESSOR_CHECK_OUT_MODE,
  STRESSOR_CHECK_PAUSE_MODE,
  STRESSOR_CHECK_RESUME_MODE,
  STRESSOR_NOT_APPLY_METADATA,
} from "./constants";
import { processErrorOnMutationCustome } from "../../../../util/errorMessagingCustome";
import { useSelectedProcedure } from "../../../dataEgress/dataEgressMutations";
import { useStressrunFileList, useUnitVersion } from "./stressEntryQueries";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      backgroundColor: theme.palette.grey[200],
      minHeight: 100,

      position: "relative",
      overflow: "auto",
    },
  }),
);

const useFocus = () => {
  const htmlElRef = React.useRef(null);
  const setFocus = () => {
    htmlElRef.current && (htmlElRef.current as any).focus();
  };

  return { htmlElRef, setFocus };
};

export const SerialNumberScanner = () => {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const { state, dispatch } = useStressEntryContext();
  const { dispatch: authDispatch } = useAuthContext();
  const { authAxios } = useFetchContext();
  const { htmlElRef, setFocus } = useFocus();

  const [procedureResultIds, setProcedureResultIds] = React.useState<number[]>(
    [],
  );

  const {
    mutateAsync: mutateValidateAssetSerial,
    error: errorMutateValidateAsset,
    isError: isErrorMutateValidateAsset,
    isSuccess: isSuccessValidateAsset,
    isLoading: isLoadingValidateAsset,
  } = useValidateAssetSerial();

  const {
    data: units,
    isSuccess: isSuccessUnits,
    isLoading: isLoadingUnits,
    isError: isErrorUnits,
    error: errorUnits,
  } = useUnitsBySerialNumber(state.submittedSerialNumber);

  const { mutateAsync: mutate } = postPauseReume();

  const {
    mutateAsync: fetchSelectedStress,
    isLoading: isLoadingProcedure,
    isError: isErrorProcedure,
    error: errorProcedure,
  } = useSelectedProcedure();

  const {
    data: unitVersionData,
    isSuccess: isSuccessUnitVersion,
    isLoading: isLoadingUnitversion,
    isError: isErrorUnitVersion,
    error: errorUnitVersion,
  } = useUnitVersion(
    procedureResultIds.length ? procedureResultIds : undefined,
  );
  
    const { mutateAsync: mutateStressFileList, isLoading: isLoadingStressFiles } =
    useStressrunFileList();

  React.useEffect(() => {
    if (isSuccessUnitVersion && unitVersionData) {
      dispatch({
        type: "SET_UNIT_VERSION",
        payload: { unitVersion: unitVersionData },
      });
    }
  }, [isSuccessUnitVersion, unitVersionData]);

  React.useEffect(() => {
    if (
      state.submittedSerialNumber &&
      state.submittedSerialNumber !== "" &&
      isSuccessUnits &&
      units.length === 0
    ) {
      toast.error("Invalid serial number.Please type another one.");

      setFocus();
    } else if (
      state.submittedSerialNumber &&
      state.submittedSerialNumber !== "" &&
      units &&
      units.length !== 0
    ) {
      dispatch({ type: "ADD_SCANNED_UNIT", payload: { unit: units[0] } });

      setFocus();
    }
  }, [dispatch, isSuccessUnits, setFocus, state.submittedSerialNumber, units]);

  const getStressFiles = async (ids: number[]) => {
    if (!ids.length) return;

    try {
      const response = await mutateStressFileList({
        authAxios,
        procedure_result_ids: ids,
      });

      dispatch({
        type: "SET_ACTION_API_RESPONSE",
        payload: {
          actionApiResponse: response,
        },
      });
    } catch (error) {
      console.error("Error fetching stress files:", error);
      toast.error("Unable to fetch stress files.");
    }
  };

  const listProcedures = [
    "damp_heat",
    "thermal_cycling",
    "CID_TEST",
    "LETID_TEST",
    "light_soak",
    "PIDTest",
    "HailTest",
    "UVIDTest",
    "HumidityFreezeTest",
    "DML",
    "SML",
  ];

  // if (isErrorMutateValidateAsset) {
  //   return <ErrorMessage error={errorMutateValidateAsset} />;
  // }
  if (isLoadingUnits) {
    return (
      <Backdrop open={isLoadingUnits}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isErrorUnits) {
    return <ErrorMessage error={errorUnits} />;
  }

  if (!state.selectedStressProcedure && state.mode === STRESSOR_CHECK_IN_MODE) {
    return null;
  }

  if (!state.selectedStressType && state.mode === STRESSOR_CHECK_OUT_MODE) {
    return null;
  }

  if (!state.selectedStressType && state.mode === STRESSOR_CHECK_PAUSE_MODE) {
    return null;
  }

  if (!state.selectedStressType && state.mode === STRESSOR_CHECK_RESUME_MODE) {
    return null;
  }

  const checkUnits = async (e: any) => {
    try {
      const collectedProcedures: {
        procedureResultId: number;
        procedureDefinitionName: string;
      }[] = [];

      const collectedIds: any[] = [];
      for (let [key, value] of state.scannedUnits) {
        if (state.checkedUnits.has(key)) {
          toast.error(`Serial number ${key} already checked.`);
        } else {
          let mutateResult: string | any[];
          if (state.mode === STRESSOR_CHECK_IN_MODE) {
            mutateResult = await mutateValidateAssetSerial({
              authAxios,
              flag: 1,
              asset_name: state.selectedStressType.name,
              procedure_definition: state.selectedStressProcedure.id,
              unitId: value.id,
            });
          } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
            try {
              const testTypeResult = await fetchSelectedStress({
                authAxios,
                serial_number: key,
              });

              console.log("Test Type API result:", testTypeResult);
              switch (testTypeResult.test_type) {
                case listProcedures[0]:
                  dispatch({ type: "SET_IS_DAMP_HEAT_CHECK", payload: true });
                  break;
                case listProcedures[1]:
                  dispatch({
                    type: "SET_IS_THERMAL_CYCLING_CHECK",
                    payload: true,
                  });
                  break;
                case listProcedures[2]:
                  dispatch({ type: "SET_IS_CID_TEST_CHECK", payload: true });
                  break;
                case listProcedures[3]:
                  dispatch({ type: "SET_IS_LETID_TEST_CHECK", payload: true });
                  break;
                case listProcedures[4]:
                  dispatch({ type: "SET_IS_LIGHT_SOAK_CHECK", payload: true });
                  break;

                case listProcedures[5]:
                  dispatch({ type: "SET_IS_PID_CHECK", payload: true });
                  break;

                case listProcedures[6]:
                  dispatch({ type: "SET_IS_HAIL_CHECK", payload: true });
                  break;

                case listProcedures[7]:
                  dispatch({ type: "SET_IS_UVID_CHECK", payload: true });
                  break;

                case listProcedures[8]:
                  dispatch({ type: "SET_IS_HUMIDITY_CHECK", payload: true });
                  break;
                case listProcedures[9]:
                  dispatch({ type: "SET_IS_DML_CHECK", payload: true });
                  break;
                case listProcedures[10]:
                  dispatch({ type: "SET_IS_SML_CHECK", payload: true });
                  break;
                default:
                  break;
              }
            } catch (e) {
              toast.error(`Error fetching test type for ${key}`);
              return;
            }
            mutateResult = await mutateValidateAssetSerial({
              authAxios,
              flag: 4,
              asset_name: state.selectedStressType.name,
              unitId: value.id,
            });
          } else if (state.mode === STRESSOR_CHECK_PAUSE_MODE) {
            mutateResult = await mutateValidateAssetSerial({
              authAxios,
              flag: 2,
              asset_name: state.selectedStressType.name,
              unitId: value.id,
            });
          } else if (state.mode === STRESSOR_CHECK_RESUME_MODE) {
            mutateResult = await mutateValidateAssetSerial({
              authAxios,
              flag: 3,
              asset_name: state.selectedStressType.name,
              unitId: value.id,
            });
          } else {
            mutateResult = await mutateValidateAssetSerial({
              authAxios,
              asset_name: state.selectedStressType.name,
              unitId: value.id,
            });
          }

          if (mutateResult && mutateResult.length === 0) {
            toast.error(
              "This PV module has nothing to do here!.Please review it in the scanned list and modify it.",
            );
          } else {
            const procedureResult = mutateResult[0];
            const currentMode =
              state.mode === STRESSOR_CHECK_IN_MODE
                ? STEP_RESULT_TEST_START_NAME
                : state.mode === STRESSOR_CHECK_OUT_MODE
                  ? STEP_RESULT_TEST_END_NAME
                  : state.mode === STRESSOR_CHECK_PAUSE_MODE
                    ? STEP_RESULT_TEST_PAUSE_NAME
                    : STEP_RESULT_TEST_RESUME_NAME;
            // state.mode === STRESSOR_CHECK_IN_MODE ? STEP_RESULT_TEST_START_NAME
            // : STEP_RESULT_TEST_END_NAME

            let stepResult = find(procedureResult.step_results, [
              "name",
              currentMode,
            ]);

            if (!stepResult) {
              toast.error(
                `The unit ${key} does not have work for the selected procedure.Please review it in the scanned list and modify it.`,
              );
            } else {
              const linearExecutionGroup = stepResult.linear_execution_group;
              const stepResultSibling = filter(
                procedureResult.step_results,
                (stepResult) =>
                  stepResult.linear_execution_group === linearExecutionGroup &&
                  stepResult.name !== currentMode,
              );
              dispatch({
                type: "ADD_CHECKED_UNIT",
                payload: {
                  unit: value,
                  procedureResult: mutateResult[0],
                  sibling: stepResultSibling[0] || STRESSOR_NOT_APPLY_METADATA,
                  siblings: stepResultSibling.length
                    ? stepResultSibling
                    : undefined, // new, additive
                },
              });
              dispatch({ type: "REMOVE_SCANNED_UNIT", payload: { id: key } });
            }
          }
          if (Array.isArray(mutateResult) && mutateResult.length > 0) {
            console.log("Mutate Result:", mutateResult);

            if (state.mode === STRESSOR_CHECK_IN_MODE) {
              // Stress Entry
              const ids = mutateResult.map((r: any) => r.id);

              console.log("Stress Entry IDs:", ids);

              collectedIds.push(...ids);
            } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
              // Stress Exit
              const inProgressProcedure = mutateResult.find(
                (r: any) => r.disposition_name === "In Progress",
              );

              if (inProgressProcedure) {
                collectedProcedures.push({
                  procedureResultId: inProgressProcedure.id,
                  procedureDefinitionName:
                    inProgressProcedure.procedure_definition_name,
                });
              }
            } else {
              const ids = mutateResult.map((r: any) => r.id);

              console.log("Stress Entry IDs:", ids);

              collectedIds.push(...ids);
            }
          }
        }
      }
      if (state.mode === STRESSOR_CHECK_IN_MODE) {
        if (collectedIds.length > 0) {
          const uniqueIds = Array.from(new Set(collectedIds));

          const procedures = uniqueIds.map((id) => ({
            procedureResultId: id,
            procedureDefinitionName: "",
          }));

          console.log("Stress Entry IDs:", uniqueIds);

          // For API
          setProcedureResultIds(uniqueIds);

          // For context/reducer
          dispatch({
            type: "SET_PROCEDURE_RESULT_IDS",
            payload: {
              procedures,
            },
          });
        }
      } else if (state.mode === STRESSOR_CHECK_OUT_MODE) {
        // ==============================
        // STRESS EXIT
        // ==============================

        if (collectedProcedures.length > 0) {
          const uniqueProcedures = Array.from(
            new Map(
              collectedProcedures.map((item) => [item.procedureResultId, item]),
            ).values(),
          );

          const procedureResultIds = uniqueProcedures.map(
            (item) => item.procedureResultId,
          );

          console.log("Stress Exit IDs:", procedureResultIds);

          setProcedureResultIds(procedureResultIds);
             await getStressFiles(procedureResultIds);

          dispatch({
            type: "SET_PROCEDURE_RESULT_IDS",
            payload: {
              procedures: uniqueProcedures,
            },
          });
        }
      } else {
        if (collectedIds.length > 0) {
          const uniqueIds = Array.from(new Set(collectedIds));

          const procedures = uniqueIds.map((id) => ({
            procedureResultId: id,
            procedureDefinitionName: "",
          }));

          

          // For API
          setProcedureResultIds(uniqueIds);

          // For context/reducer
          dispatch({
            type: "SET_PROCEDURE_RESULT_IDS",
            payload: {
              procedures,
            },
          });
          await getStressFiles(uniqueIds);
        }
      }
    } catch (err) {
      // toast.error("Error while executing validating asset routine.");
      processErrorOnMutationCustome(err, authDispatch, history);
    }
  };

  return (
    <Grid container direction="row" alignItems="center">
      <Grid item xs={12}>
        <Formik
          initialValues={{
            serialNumber: "",
          }}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object({
            serialNumber: Yup.string()
              .required("Field must be required")
              .test(
                "serial-number-dup",
                "Serial number already scanned",
                (value) => (value ? !state.scannedUnits.has(value) : false),
              ),
          })}
          onSubmit={async (values, actions) => {
            await actions.validateForm();
            if (state.checkedUnits.has(values.serialNumber)) {
              toast.error(
                `Serial number ${values.serialNumber} already checked.`,
              );
            } else {
              dispatch({
                type: "SET_SUBMITTED_SERIAL_NUMBER",
                payload: { serialNumber: values.serialNumber },
              });
            }
            console.log(values);
            actions.setFieldValue("serialNumber", "");
          }}
        >
          {({ errors, touched, submitForm, resetForm }) => {
            return (
              <div style={theme.container}>
                <Form>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={2}
                  >
                    <Grid item xs={3}>
                      <Field
                        id="serialNumber"
                        name="serialNumber"
                        helperText={
                          touched.serialNumber ? errors.serialNumber : ""
                        }
                        error={
                          touched.serialNumber && Boolean(errors.serialNumber)
                        }
                        component={FormikTextField}
                        data-testid="serialNumber"
                        label="Serial Number"
                        margin="dense"
                        InputProps={{
                          inputRef: htmlElRef,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        data-testid="scanSerialNumber"
                        variant="contained"
                        color="primary"
                        onClick={submitForm}
                        endIcon={<AddIcon>Add</AddIcon>}
                      >
                        Scan
                      </Button>
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        data-testid="clearSerialNumber"
                        variant="contained"
                        color="secondary"
                        onClick={() => resetForm()}
                        endIcon={<ClearIcon>Clear</ClearIcon>}
                      >
                        Clear
                      </Button>
                    </Grid>
                    {state.scannedUnits.size > 0 && (
                      <Grid item xs={3}>
                        {state.mode === STRESSOR_CHECK_PAUSE_MODE ||
                        state.mode === STRESSOR_CHECK_RESUME_MODE ? (
                          // <div>
                          //   <Formik
                          //     initialValues={{
                          //       comments: "",
                          //     }}
                          //     onSubmit={async (values, { setSubmitting }) => {
                          //       try {
                          //         const status = state.mode === STRESSOR_CHECK_PAUSE_MODE ? "pause" : "resume";
                          //         const data = await mutate({
                          //           serial_number: state.submittedSerialNumber,
                          //           status,
                          //           comments: values.comments,
                          //         });

                          //         setSubmitting(false);
                          //         toast.success(`${status.charAt(0).toUpperCase() + status.slice(1)}d successfully`);
                          //         window.location.reload()
                          //       } catch (error) {
                          //         toast.error(`Error while modifying project.`);
                          //         processErrorOnMutation(error, dispatch, history);
                          //       }
                          //     }}
                          //   >
                          //     {({ submitForm , resetForm}) => (
                          //       <Form>
                          //         <Grid
                          //           container
                          //           direction="row"
                          //           justify="flex-start"
                          //           alignItems="center"
                          //           spacing={5}
                          //         >
                          //           <Grid item xs={10}>
                          //             <Field
                          //               id="comments"
                          //               type="text"
                          //               name="comments"
                          //               component={FormikTextField}
                          //               label="Comments"
                          //               data-testid="comments"
                          //               InputProps={{
                          //                 inputRef: htmlElRef,
                          //               }}
                          //               InputLabelProps={{
                          //                 shrink: true,
                          //               }}
                          //             />
                          //           </Grid>
                          //           <Grid item xs={2}>
                          //             <Button
                          //               data-testid="submitSerialNumber"
                          //               variant="contained"
                          //               color="primary"

                          //               onClick={submitForm}
                          //               endIcon={<CheckIcon />}
                          //               style={{ marginLeft: 32 }}
                          //             >
                          //               {state.mode === STRESSOR_CHECK_PAUSE_MODE ? "Pause" : "Resume"}
                          //             </Button>
                          //           </Grid>
                          //         </Grid>
                          //       </Form>
                          //     )}
                          //   </Formik>
                          // </div>
                          <Button
                            data-testid="submitSerialNumber"
                            variant="contained"
                            color="primary"
                            onClick={checkUnits}
                            endIcon={<CheckIcon />}
                            style={{ marginLeft: 32 }}
                          >
                            {state.mode === STRESSOR_CHECK_PAUSE_MODE
                              ? "Pause"
                              : "Resume"}
                          </Button>
                        ) : (
                          <Button
                            data-testid="submitSerialNumber"
                            variant="contained"
                            style={theme.btnSubmit}
                            onClick={checkUnits}
                            endIcon={<CheckIcon>Check</CheckIcon>}
                          >
                            {state.mode === STRESSOR_CHECK_IN_MODE
                              ? " Check-in"
                              : "Check-out"}
                          </Button>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={2}>
                      {isLoadingValidateAsset && (
                        <CircularProgress
                          color="inherit"
                          style={{ textAlign: "left" }}
                        />
                      )}
                    </Grid>
                  </Grid>
                  <FormFocusError />
                </Form>
              </div>
            );
          }}
        </Formik>
      </Grid>
      <Grid item xs={11}>
        {state.scannedUnits.size > 0 && (
          <>
            <Typography variant="h6">Serial Number Model</Typography>
            <List
              component="nav"
              aria-label="main serial number folders"
              className={classes.root}
            >
              <ListItem dense></ListItem>
              {[...state.scannedUnits].map(([key, value]) => {
                return (
                  <ListItem dense>
                    <ListItemText
                      primary={`${
                        value.serial_number || "Not Available"
                      }         ${value.model || "Not Available"}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => {
                          dispatch({
                            type: "REMOVE_SCANNED_UNIT",
                            payload: { id: key },
                          });
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Grid>
      <Grid item xs={1}></Grid>
    </Grid>
  );
};
